import React, { useMemo } from 'react';
import { getGameData } from '../services/dataLoader';
import { checkAnswer } from '../services/templatesHelper';
import { TEMPLATES } from '../services/templates';
import { TRIVIA_QUESTIONS } from '../services/triviaQuestionBank';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';

type AnswerInfo = {
  name: string;
  iso3: string;
};

type QuestionAudit = {
  id: string;
  text: string;
  templateId: string;
  answers: AnswerInfo[];
};

const getCountryNameLength = (name: string): number => name.replace(/[^a-z]/gi, '').length;

const getLengthBuckets = (countries: { name: string }[]): Record<number, { name: string }[]> =>
  countries.reduce<Record<number, { name: string }[]>>((acc, country) => {
    const length = getCountryNameLength(country.name);
    if (!acc[length]) acc[length] = [];
    acc[length].push(country);
    return acc;
  }, {});

const toCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

const downloadFile = (content: string, filename: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const AuditPage: React.FC = () => {
  const data = getGameData();

  const countries = useMemo(() => {
    return [...data.countries].sort((a, b) => a.name.localeCompare(b.name));
  }, [data.countries]);

  const questionAudits = useMemo<QuestionAudit[]>(() => {
    const templateMap = new Map(TEMPLATES.map((template) => [template.id, template]));
    const audits: QuestionAudit[] = [];

    const addQuestion = (templateId: string, subject: string | number | null, labelParam?: any) => {
      const template = templateMap.get(templateId);
      if (!template) return;
      const text = template.template(labelParam ?? subject);
      const answers = data.countries
        .filter((country) => checkAnswer(templateId, subject, country, data))
        .map((country) => ({ name: country.name, iso3: country.iso3 }));
      audits.push({
        id: `${templateId}-${String(subject ?? 'none')}`,
        text,
        templateId,
        answers,
      });
    };

    const letters = Object.keys(data.indexes.byFirstLetter).sort();
    letters.forEach((letter) => addQuestion('starts_with', letter));
    addQuestion('ends_with_ia', null);
    addQuestion('starts_with_vowel', null);
    addQuestion('long_name', 10);

    const lengthBuckets = getLengthBuckets(data.countries);
    Object.keys(lengthBuckets)
      .map(Number)
      .filter((length) => lengthBuckets[length].length >= 3)
      .forEach((length) => addQuestion('exact_length', length));

    Object.keys(lengthBuckets)
      .map(Number)
      .filter((length) => length <= 6 && lengthBuckets[length].length >= 2)
      .forEach((length) => addQuestion('short_name', length));

    data.countries.forEach((country) => addQuestion('same_first_letter_as', country.iso3, country));

    const regions = Object.keys(data.indexes.byRegion).sort();
    regions.forEach((region) => addQuestion('in_region', region));

    const borderCandidates = data.countries.filter((country) =>
      country.borders.some((border) => Boolean(data.indexes.byIso3[border]))
    );
    borderCandidates.forEach((country) => addQuestion('borders_country', country.iso3, country));
    addQuestion('has_no_borders', null);
    addQuestion('many_borders', 4);
    borderCandidates.forEach((country) => addQuestion('shares_border_with', country.iso3, country));

    Object.keys(data.indexes.byCurrencyCode)
      .sort()
      .forEach((code) => addQuestion('uses_currency', code));
    Object.keys(data.indexes.byCurrencyGroup)
      .sort()
      .forEach((group) => addQuestion('currency_name_group', group));
    Object.keys(data.indexes.byFractionalUnit)
      .sort()
      .forEach((unit) => addQuestion('fractional_unit', unit));
    addQuestion('no_fractional_unit', null);
    addQuestion('no_own_currency', null);

    TRIVIA_QUESTIONS.forEach((question) => {
      const answers = data.indexes.byTriviaTag[question.id] ?? [];
      if (answers.length === 0) return;
      addQuestion(`trivia_${question.id}`, question.id);
    });

    return audits;
  }, [data]);

  const handleExportCsv = () => {
    const headers = [
      'name',
      'iso2',
      'iso3',
      'region',
      'subregion',
      'borders',
      'altSpellings',
      'nativeNames',
    ];
    const rows = countries.map((country) => [
      country.name,
      country.iso2,
      country.iso3,
      country.region,
      country.subregion,
      country.borders.join('; '),
      country.altSpellings.join('; '),
      country.nativeNames.join('; '),
    ]);
    const csv = [headers.map(toCsvValue).join(','), ...rows.map((row) => row.map(toCsvValue).join(','))].join('\n');
    downloadFile(csv, 'countries.csv', 'text/csv');
  };

  const handleExportJson = () => {
    downloadFile(JSON.stringify(countries, null, 2), 'countries.json', 'application/json');
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Audit & Export</h2>
          <p className="text-sm text-muted-foreground">
            Review the country dataset, facets, and all question answer pools.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportCsv}>
            Export Countries CSV
          </Button>
          <Button variant="outline" onClick={handleExportJson}>
            Export Countries JSON
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Country Facets</h3>
          <p className="text-xs text-muted-foreground">
            Full list of countries and their region/subregion facets, borders, and alternate names.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="pb-2 pr-3 font-semibold">Country</th>
                  <th className="pb-2 pr-3 font-semibold">ISO2</th>
                  <th className="pb-2 pr-3 font-semibold">ISO3</th>
                  <th className="pb-2 pr-3 font-semibold">Region</th>
                  <th className="pb-2 pr-3 font-semibold">Subregion</th>
                  <th className="pb-2 pr-3 font-semibold">Borders</th>
                  <th className="pb-2 pr-3 font-semibold">Alt Spellings</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((country) => (
                  <tr key={country.iso3} className="border-t border-border/60">
                    <td className="py-2 pr-3 font-medium">{country.name}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{country.iso2}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{country.iso3}</td>
                    <td className="py-2 pr-3">{country.region}</td>
                    <td className="py-2 pr-3">{country.subregion}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {country.borders.length > 0 ? country.borders.join(', ') : 'None'}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {country.altSpellings.length > 0 ? country.altSpellings.join(', ') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Questions & Answers</h3>
        <p className="text-xs text-muted-foreground">
          Each question below lists the possible answers with the country name and key (ISO3).
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {questionAudits.map((audit) => (
            <Card key={audit.id} className="h-full">
              <CardHeader>
                <h4 className="text-sm font-semibold text-foreground">{audit.text}</h4>
                <span className="text-xs text-muted-foreground">
                  {audit.answers.length} answers • Template: {audit.templateId}
                </span>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-wrap gap-2">
                  {audit.answers.map((answer) => (
                    <span
                      key={`${audit.id}-${answer.iso3}`}
                      className="rounded-full border border-border/70 bg-muted/60 px-2 py-1 text-[11px] text-foreground"
                    >
                      {answer.name} ({answer.iso3})
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
